import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import accountsAPI from '@/api/accounts';
import {
  generateCheckoutBaseUrl,
  getTierByTotalProfiles,
} from '@/helpers/sealSubscriptions';
import useAuth from '@/hooks/useAuth';

/**
 * Custom hook for memoized subscription checkout URL generation
 * Uses direct API call to ensure fresh totalProfiles data for accurate tier calculation
 */
export function useSubscriptionCheckoutUrl() {
  const { user, orgID } = useAuth();

  // Fetch fresh subscription data to get accurate totalProfiles
  const { data: subscriptionData } = useQuery({
    queryKey: ['getSubscription', orgID],
    queryFn: async () => {
      if (!orgID) return null;
      const res = await accountsAPI.getSubscription(orgID);
      return res.data;
    },
    enabled: Boolean(orgID),
  });

  const checkoutUrl = useMemo(() => {
    try {
      if (!user || !subscriptionData) {
        return '';
      }

      // Use fresh totalProfiles from API to ensure accurate tier calculation
      const safeProfileCount =
        subscriptionData.total_profiles > 0
          ? subscriptionData.total_profiles
          : 1;
      const tier = getTierByTotalProfiles(safeProfileCount);

      if (!tier) {
        console.warn(
          `useSubscriptionCheckoutUrl: No tier found for ${safeProfileCount} profiles`,
        );
        return '';
      }

      const attributes = {
        'checkout[email]': user.email,
        'checkout[billing_address][first_name]': user.first_name,
        'checkout[billing_address][last_name]': user.last_name,
      };

      return generateCheckoutBaseUrl(tier.variant_id, attributes);
    } catch (error) {
      console.error(
        'useSubscriptionCheckoutUrl: Error generating checkout URL',
        error,
      );
      return '';
    }
  }, [user, subscriptionData]);

  const isCheckoutUrlReady = useMemo(() => {
    return Boolean(user && subscriptionData && checkoutUrl);
  }, [user, subscriptionData, checkoutUrl]);

  return {
    checkoutUrl,
    isCheckoutUrlReady,
    totalProfiles: subscriptionData?.total_profiles ?? 1,
  };
}

export default useSubscriptionCheckoutUrl;
